// src/hooks/useTheme.ts -- Slice J / Phase 2, extended 2026-11-04 (Dark Mode).
//
// Reads the user's theme preference from /api/me and publishes the skin's
// tokens as CSS custom properties on document.documentElement so components
// reading var(--c-accent, ...) etc. (see src/theme.ts) pick up the new values
// on the next render.
//
// Mounted once at the top of the authed app shell (App.tsx). Default theme is
// "warm-gold" -- the current Info Hub palette, so existing users see no visual
// change until they switch. The last applied skin is cached in localStorage
// and re-applied synchronously on mount so a Dark Mode user never sees a
// cream flash while /api/me is in flight.

import { useEffect } from "react";
import { useCurrentUser } from "../auth";
import {
  DEFAULT_THEME,
  THEMES,
  applyThemeToDocument,
  isThemeKey,
  readCachedTheme,
  writeCachedTheme,
} from "../lib/themes";
import type { ThemeKey } from "../lib/themes";

export function useTheme(): void {
  const { me } = useCurrentUser();
  const serverTheme = me?.preferences?.theme;
  const theme: ThemeKey | null = me
    ? (isThemeKey(serverTheme) ? serverTheme : DEFAULT_THEME)
    : null;

  // First paint: whatever skin this browser used last (or the default).
  useEffect(() => {
    applyThemeToDocument(readCachedTheme() ?? DEFAULT_THEME);
  }, []);

  // Once /api/me lands: the user's saved skin wins and is cached for next time.
  useEffect(() => {
    if (!theme) return;
    applyThemeToDocument(theme);
    writeCachedTheme(theme);
  }, [theme]);
}

// Re-export so callers can `import { THEMES, ThemeKey } from "../hooks/useTheme"`
// without a second import if they prefer. Optional convenience.
export { THEMES };
export type { ThemeKey };
